/* eslint-disable no-use-before-define */
import { EntityTypeEnum } from './entity';
import { NatureEnum } from './nature';
import { UserOverview } from './user';

/**
 * Template types
 */
export type CWTSchemaBase = {
    id: string;
    name: string;
    order: number;
    description: string | null;
    readOnly: boolean;
};

export type CWTSchemaProperty =
    | CWTSchemaList
    | CWTSchemaListItem
    | CWTSchemaSectionGroup
    | CWTSchemaSection
    | CWTSchemaTable
    | CWTSchemaTableRow
    | CWTSchemaField
    | (CWTSchemaField & { styles: TableRowStyles });

export type ConditionCheck = {
    lhs: string | number | boolean | null | CWTSchemaCalculation;
    operation: '>' | '>=' | '<' | '<=' | '=' | '!=';
    rhs: string | number | boolean | null | CWTSchemaCalculation;
};

export type ConditionOr = { or: (ConditionCheck | ConditionOr | ConditionAnd)[] };

export type ConditionAnd = { and: (ConditionCheck | ConditionOr | ConditionAnd)[] };

export type ConditionIf = ConditionCheck | ConditionAnd | ConditionOr;

export type ConditionBlock = {
    return: number | string | boolean | null | CWTSchemaCalculation;
};

export type CWTSchemaCondition = {
    type: 'CONDITION';
    return: {
        if: ConditionIf;
        then: ConditionBlock;
        else: ConditionBlock;
    };
};

export type CWTSchemaAggregation = {
    type: 'AGGREGATION';
    return: {
        operation: string;
        params: CWTRef[];
    };
};

export type CWTSchemaFunction = {
    type: 'FUNCTION';
    return: { operation: string; params: (string | number | null | CWTSchemaCalculation)[] };
};
export type CWTSchemaExpression = {
    type: 'EXPRESSION';
    return: { operation: '*' | '+' | '-' | '/' | '^'; params: (string | number | CWTSchemaCalculation)[] };
};

export type CWTRef = { ref: string };

export type CWTFieldReconciliation = {
    defaultDate: string | null;
    defaultDescription: string | null;
    positiveNature: NatureEnum;
};

export type CWTSchemaCalculation = CWTSchemaFunction | CWTSchemaExpression | CWTSchemaCondition | CWTRef | CWTSchemaAggregation;

export type QuickActionType = 'CREATE_ENTRY';

export type EntryQuickAction = {
    type: 'CREATE_ENTRY';
    payload: {
        defaultDate: string | null;
        defaultDescription: string | null;
        positiveNature: NatureEnum;
    };
};

export type QuickAction = EntryQuickAction;

export type LinkedResourceType = 'ENTRY';

export type LinkedEntry = {
    type: 'ENTRY';
    id: string;
    metadata: {
        worksheetId: string;
        reference: number;
    };
};

export type LinkedResource = LinkedEntry;

export type CWTSchemaField = CWTSchemaBase & {
    helperText: string | null;
    defaultValue: string | number | null;
    pinned: boolean;
    canPin: boolean;
    canRename: boolean;
    dependentFields: CWTRef[];
    calculation: CWTSchemaCalculation | null;
    type: FieldType;
    reconciliation?: CWTFieldReconciliation | null;
    quickActions?: QuickAction[];
    hidden?: boolean;
};

export type CWTSchemaMoneyField = CWTSchemaField & {
    type: FieldType.MONEY;
};

export type CWTSchemaNumberField = CWTSchemaField & {
    type: FieldType.NUMBER;
};

export type CWTSchemaAccountingField = CWTSchemaField & {
    type: FieldType.ACCOUNTING;
};

export type CWTSchemaPercentField = CWTSchemaField & {
    type: FieldType.PERCENT;
};

export type CWTSchemaDateField = CWTSchemaField & {
    type: FieldType.DATE;
};

export type CWTSchemaTextField = CWTSchemaField & {
    type: FieldType.TEXT;
};

export type CWTSchemaSection = CWTSchemaBase & {
    type: SectionType.SECTION;
    summary: CWTSchemaPropertiesSummary | null;
    additionalFields: FieldType[] | null;
    properties: { [key: string]: CWTSchemaField };
};

export type CWTSchemaSectionGroup = CWTSchemaBase & {
    type: SectionType.SECTION_GROUP;
    properties: { [key: string]: CWTSchemaSection | CWTSchemaSectionGroup | CWTSchemaTable | CWTSchemaField };
};

export type CWTSchemaListItem = CWTSchemaBase & {
    type: ListType.LIST_ITEM;
    properties: { [key: string]: CWTSchemaSection | CWTSchemaSectionGroup | CWTSchemaTable | CWTSchemaField };
};

export type TextAlignment = 'left' | 'center' | 'right';

export type TableRowStyles = {
    width: string;
    alignment: TextAlignment;
};

export type CWTSchemaPropertiesSummary = { type?: 'COLUMN'; properties: { [key: string]: CWTSchemaPropertySummary | null } };

export type CWTSchemaPropertySummary = {
    id: string;
    name: string;
    type: FieldType;
    value: number | string | null;
    dependentFields: CWTRef[];
    calculation: CWTSchemaAggregation | CWTSchemaFunction;
    reconciliation: CWTFieldReconciliation | null;
    quickActions?: QuickAction[];
    linkedResources?: LinkedResource[];
};

export type CWTSchemaTable = CWTSchemaBase & {
    type: TableType.TABLE;
    summary: CWTSchemaPropertiesSummary | null;
    properties: { row: CWTSchemaTableRow };
    config: {
        minRows: number;
    } | null;
    generatedFromFunction?: GeneratorFunction | null;
};

export type CWTSchemaTableRow = CWTSchemaBase & {
    type: TableType.TABLE_ROW;
    properties: { [key: string]: CWTSchemaField & { styles: TableRowStyles } };
};

export type CWTSchemaList = CWTSchemaBase & {
    type: ListType.LIST;
    summary: CWTSchemaPropertiesSummary | null;
    properties: { item: CWTSchemaListItem };
};

export type Source = {
    provider: string;
    resource: string;
    lastSynced: Date | null;
    lastSyncAttempt: Date | null;
    syncResult:
        | {
              outcome: 'success';
          }
        | {
              outcome: 'error';
              reason: unknown;
          };
};
export type CWTSchema = {
    version: string;
    properties: { [key: string]: CWTSchemaList | CWTSchemaSectionGroup | CWTSchemaSection | CWTSchemaTable | CWTSchemaField };
    reviewConditions: { description: string; satisfied: boolean }[];
    sources?: Source[];
};

export type GeneratorFunction = {
    operation: string;
    params: unknown[];
};

export type FieldCount = { total: number; completed: number; remaining: number };

// Types for schema list
export enum ListType {
    LIST = 'LIST',
    LIST_ITEM = 'LIST_ITEM',
}

// Types for schema table
export enum TableType {
    TABLE = 'TABLE',
    TABLE_ROW = 'TABLE_ROW',
}

// Types for schema section
export enum SectionType {
    SECTION = 'SECTION',
    SECTION_GROUP = 'SECTION_GROUP',
}

// Types for schema field
export enum FieldType {
    MONEY = 'MONEY',
    NUMBER = 'NUMBER',
    ACCOUNTING = 'ACCOUNTING',
    PERCENT = 'PERCENT',
    DATE = 'DATE',
    DROPDOWN = 'DROPDOWN',
    TEXT = 'TEXT',
}

export type WorksheetSchemaProperty = WorksheetSchemaBlockProperty | WorksheetSchemaField;

export type WorksheetSchemaBlockProperty =
    | WorksheetSchemaList
    | WorksheetSchemaListItem
    | WorksheetSchemaSectionGroup
    | WorksheetSchemaSection
    | WorksheetSchemaTable
    | WorksheetSchemaTableRow;

export type WorksheetSchema = {
    version: string;
    properties: {
        [key: string]:
            | WorksheetSchemaSectionGroup
            | WorksheetSchemaList
            | WorksheetSchemaSection
            | WorksheetSchemaField
            | WorksheetSchemaTable;
    };
    reviewConditions: { description: string; satisfied: boolean }[];
    sources?: Source[];
};

export type WorksheetSchemaBase = {
    id: string;
    name: string;
    order: number;
    description: string | null;
    readOnly: boolean;
};

export type WorksheetSchemaTable = WorksheetSchemaBase & {
    type: TableType.TABLE;
    row: CWTSchemaTableRow;
    properties: WorksheetSchemaTableRow[];
    summary: CWTSchemaPropertiesSummary | null;
    config: {
        minRows: number;
    } | null;
    generatedFromFunction?: GeneratorFunction | null;
    dependentFields?: CWTRef[];
};

export type WorksheetSchemaTableRow = WorksheetSchemaBase & {
    type: TableType.TABLE_ROW;
    properties: { [key: string]: WorksheetSchemaField & { styles: TableRowStyles } };
};

export type WorksheetSchemaList = WorksheetSchemaBase & {
    type: ListType.LIST;
    item: CWTSchemaListItem;
    summary: CWTSchemaPropertiesSummary | null;
    properties: WorksheetSchemaListItem[];
};

export type WorksheetSchemaListItem = WorksheetSchemaBase & {
    type: ListType.LIST_ITEM;
    properties: { [key: string]: WorksheetSchemaSection | WorksheetSchemaSectionGroup | WorksheetSchemaTable | WorksheetSchemaField };
};

export type WorksheetSchemaSectionGroup = WorksheetSchemaBase & {
    type: SectionType.SECTION_GROUP;
    properties: { [key: string]: WorksheetSchemaSection | WorksheetSchemaSectionGroup | WorksheetSchemaTable | WorksheetSchemaField };
};

export type WorksheetSchemaSection = WorksheetSchemaBase & {
    type: SectionType.SECTION;
    properties: { [key: string]: WorksheetSchemaField };
    summary: CWTSchemaPropertiesSummary | null;
    additionalFields: FieldType[] | null;
};

export type WorksheetSchemaFieldBase = WorksheetSchemaBase & {
    helperText: string | null;
    defaultValue: string | number | null;
    pinned: boolean;
    dependentFields: CWTRef[];
    calculation: CWTSchemaCalculation | CWTRef | null;
    overridden: boolean;
    isAdditionalField: boolean;
    hidden?: boolean;
    reconciliation: (CWTFieldReconciliation & { entryId: string | null }) | null;
    quickActions?: QuickAction[];
    linkedResources?: LinkedResource[];
};

export type WorksheetMoneyField = WorksheetSchemaFieldBase & {
    type: FieldType.MONEY;
    value: number | null;
};

export type WorksheetNumberField = WorksheetSchemaFieldBase & {
    type: FieldType.NUMBER;
    value: number | null;
};

export type WorksheetAccountingField = WorksheetSchemaFieldBase & {
    type: FieldType.ACCOUNTING;
    value: number | null;
};

export type WorksheetPercentField = WorksheetSchemaFieldBase & {
    type: FieldType.PERCENT;
    value: number | null;
};

export type WorksheetDateField = WorksheetSchemaFieldBase & {
    type: FieldType.DATE;
    value: string | null;
};

export type WorksheetTextField = WorksheetSchemaFieldBase & {
    type: FieldType.TEXT;
    value: string | null;
};

export type WorksheetSchemaField =
    | WorksheetMoneyField
    | WorksheetNumberField
    | WorksheetAccountingField
    | WorksheetPercentField
    | WorksheetDateField
    | WorksheetTextField;

export type CustomWorksheetTemplateStatus = 'UNPUBLISHED' | 'PUBLISHED' | 'ARCHIVED';

export enum CWTTypeEnum {
    CUSTOM_WORKSHEET = 'CUSTOM_WORKSHEET',
    ACCOUNT_CALCULATION = 'ACCOUNT_CALCULATION',
}

export enum CWTBehaviourEnum {
    SHARE = 'SHARE',
    COPY = 'COPY',
}

export type CustomWorksheetTemplate = {
    id: string;
    name: string;
    description: string | null;
    tags: string[];
    schema: CWTSchema;
    status: CustomWorksheetTemplateStatus;
    createdBy: UserOverview | null;
    publishedBy: UserOverview | null;
    createdAt: Date;
    updatedAt: Date;
    system: boolean;
    entityTypes: EntityTypeEnum[];
    allowAsSchedule: boolean;
    behaviour: CWTBehaviourEnum;
};

export type CustomWorksheetTemplateOverview = Omit<CustomWorksheetTemplate, 'schema'>;
